import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import baseUrl from "../../../api/baseUrl";
import FormDate from "../../../components/formFeild/FormDate";
import TreatmentReportCharts, {
  TreatmentBreakdownItem,
  TreatmentTrendPoint,
} from "./TreatmentReportCharts";

type FormData = {
  startDate: string;
  endDate: string;
};

type DatePreset = "this-month" | "last-month" | "last-30" | "this-year" | "all";

type TreatmentSummary = {
  procedureCount?: number;
  caseCount?: number;
  totalValue?: number;
  paidCount?: number;
  plannedCount?: number;
  ongoingCount?: number;
  completeCount?: number;
  breakdown?: TreatmentBreakdownItem[];
  trend?: TreatmentTrendPoint[];
};

type TreatmentLine = {
  lineDate?: string;
  treatmentName?: string;
  status?: string;
  quantity?: number;
  price?: string;
  lineValue?: number;
  paid?: boolean;
  patientName?: string;
  regNo?: string;
  invoiceNo?: string;
  dentistName?: string;
  caseId?: string;
};

const DATE_PRESETS: { id: DatePreset; label: string }[] = [
  { id: "this-month", label: "This month" },
  { id: "last-month", label: "Last month" },
  { id: "last-30", label: "Last 30 days" },
  { id: "this-year", label: "This year" },
  { id: "all", label: "All time" },
];

const CHART_COLORS = [
  "#0d9488",
  "#2563eb",
  "#7c3aed",
  "#db2777",
  "#ea580c",
  "#ca8a04",
  "#16a34a",
  "#0891b2",
];

function toDateInput(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getPresetRange(preset: DatePreset): { startDate: string; endDate: string } | null {
  const today = new Date();
  const end = toDateInput(today);
  if (preset === "all") return null;
  if (preset === "this-month") {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { startDate: toDateInput(start), endDate: end };
  }
  if (preset === "last-month") {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endDate = new Date(today.getFullYear(), today.getMonth(), 0);
    return { startDate: toDateInput(start), endDate: toDateInput(endDate) };
  }
  if (preset === "last-30") {
    const start = new Date(today);
    start.setDate(start.getDate() - 29);
    return { startDate: toDateInput(start), endDate: end };
  }
  const start = new Date(today.getFullYear(), 0, 1);
  return { startDate: toDateInput(start), endDate: end };
}

function matchPreset(startDate?: string, endDate?: string): DatePreset | null {
  if (!startDate || !endDate) return "all";
  for (const preset of DATE_PRESETS) {
    const range = getPresetRange(preset.id);
    if (range?.startDate === startDate && range?.endDate === endDate) {
      return preset.id;
    }
  }
  return null;
}

function getPresetLabel(preset: DatePreset) {
  return DATE_PRESETS.find((item) => item.id === preset)?.label ?? "Custom range";
}

function formatAmount(value?: number) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatLineDate(value?: string) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function capitalizeStatus(status?: string) {
  const s = String(status || "").trim();
  if (!s) return "—";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function PanelCard({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-2xl border border-slate-200/80 bg-white shadow-sm shadow-slate-200/40 dark:border-slate-700/80 dark:bg-slate-800/60 dark:shadow-none ${className}`}
    >
      {children}
    </div>
  );
}

function SectionHeader({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: React.ElementType;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="mb-5 flex items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
        <Icon sx={{ fontSize: 20 }} />
      </div>
      <div className="min-w-0">
        <h3 className="text-sm font-semibold tracking-tight text-slate-900 dark:text-white">
          {title}
        </h3>
        {subtitle ? (
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>
        ) : null}
      </div>
    </div>
  );
}

function MetricTile({
  label,
  value,
  hint,
  accent = false,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        accent
          ? "border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.07] to-white dark:from-emerald-500/15 dark:to-slate-800/80 dark:border-emerald-500/30"
          : "border-slate-100 bg-white dark:border-slate-700/60 dark:bg-slate-800/40"
      }`}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p
        className={`mt-1.5 text-xl font-bold tracking-tight tabular-nums ${
          accent ? "text-emerald-700 dark:text-emerald-300" : "text-slate-900 dark:text-white"
        }`}
      >
        {value}
      </p>
      {hint ? (
        <p className="mt-1 text-[10px] leading-snug text-slate-400 dark:text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export default function TreatmentReportTabPanel() {
  const [summary, setSummary] = useState<TreatmentSummary>({});
  const [lines, setLines] = useState<TreatmentLine[]>([]);
  const [lineCount, setLineCount] = useState(0);
  const [linesTruncated, setLinesTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [activePreset, setActivePreset] = useState<DatePreset | null>("this-month");
  const isApplyingPreset = useRef(false);
  const methods = useForm<FormData>();
  const { watch, reset } = methods;

  const startDate = watch("startDate");
  const endDate = watch("endDate");

  const loadReport = useCallback(async (rangeStart?: string, rangeEnd?: string) => {
    setLoading(true);
    try {
      const params =
        rangeStart && rangeEnd
          ? { startDate: rangeStart, endDate: rangeEnd }
          : undefined;

      const response = await baseUrl.get("/client/account/treatment-report-summary", {
        params,
      });

      const payload = response.data?.data;
      setSummary(payload?.summary ?? {});
      setLines(payload?.lines ?? []);
      setLineCount(Number(payload?.lineCount ?? 0));
      setLinesTruncated(Boolean(payload?.linesTruncated));
    } catch (error) {
      console.error(error);
      setSummary({});
      setLines([]);
      setLineCount(0);
      setLinesTruncated(false);
      toast.error("Could not load treatment report.");
    } finally {
      setLoading(false);
    }
  }, []);

  const applyPreset = useCallback(
    (preset: DatePreset) => {
      isApplyingPreset.current = true;
      setActivePreset(preset);
      const range = getPresetRange(preset);
      if (!range) {
        reset({ startDate: "", endDate: "" });
        loadReport();
      } else {
        reset(range);
        loadReport(range.startDate, range.endDate);
      }
      window.setTimeout(() => {
        isApplyingPreset.current = false;
      }, 0);
    },
    [loadReport, reset]
  );

  useEffect(() => {
    applyPreset("this-month");
  }, [applyPreset]);

  useEffect(() => {
    const { unsubscribe } = watch((value) => {
      if (isApplyingPreset.current) return;

      if (value.startDate && value.endDate) {
        setActivePreset(matchPreset(value.startDate, value.endDate));
        loadReport(value.startDate, value.endDate);
        return;
      }

      if (!value.startDate && !value.endDate) {
        setActivePreset("all");
        loadReport();
      }
    });
    return () => unsubscribe();
  }, [watch, loadReport]);

  const breakdown = summary.breakdown ?? [];
  const trend = summary.trend ?? [];

  const rows = useMemo(() => {
    const total = breakdown.reduce((s, item) => s + (item.value || 0), 0);
    return breakdown.map((item, index) => ({
      ...item,
      color: CHART_COLORS[index % CHART_COLORS.length],
      percent: total > 0 ? ((item.value / total) * 100) : 0,
    }));
  }, [breakdown]);

  const topTreatment = rows[0] ?? null;
  const totalProcedures = Number(summary.procedureCount ?? 0);

  const insights = useMemo(() => {
    if (loading) return [];
    const result: string[] = [];

    result.push(
      lineCount === 1
        ? "1 procedure line in this period."
        : `${lineCount.toLocaleString()} procedure lines in this period.`
    );

    if (summary.caseCount != null) {
      result.push(
        `${Number(summary.caseCount).toLocaleString()} treatment case${summary.caseCount === 1 ? "" : "s"} involved.`
      );
    }

    if (topTreatment && totalProcedures > 0) {
      result.push(
        `Most common: ${topTreatment.label} (${topTreatment.percent.toFixed(1)}% of procedures).`
      );
    }

    if (Number(summary.paidCount) > 0) {
      result.push(
        `${Number(summary.paidCount).toLocaleString()} paid procedure line${summary.paidCount === 1 ? "" : "s"}.`
      );
    }

    if (Number(summary.plannedCount) > 0) {
      result.push(
        `${Number(summary.plannedCount).toLocaleString()} still marked as planned.`
      );
    }

    return result;
  }, [loading, lineCount, summary, topTreatment, totalProcedures]);

  const onSubmit: SubmitHandler<FormData> = useCallback(async (data) => {
    if (!data.startDate || !data.endDate) {
      toast.info("Select both From and To dates to download the report.");
      return;
    }
    setDownloading(true);
    try {
      const response = await baseUrl.get("/client/account/treatment-report", {
        responseType: "blob",
        params: {
          startDate: data.startDate,
          endDate: data.endDate,
        },
      });
      if (response.status === 204) {
        toast.info("No data found for the selected period.");
        return;
      }
      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(pdfBlob);
      link.download = `treatment-report-${data.startDate}-to-${data.endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(link.href);
      document.body.removeChild(link);
    } catch (error) {
      console.error(error);
      toast.error("Could not download the report.");
    } finally {
      setDownloading(false);
    }
  }, []);

  const handleReset = (e: React.MouseEvent) => {
    e.preventDefault();
    applyPreset("all");
  };

  const rangeLabel = useMemo(() => {
    if (activePreset && matchPreset(startDate, endDate) === activePreset) {
      return getPresetLabel(activePreset);
    }
    if (startDate && endDate) return `${startDate} → ${endDate}`;
    return "All time";
  }, [activePreset, startDate, endDate]);

  return (
    <div className="space-y-6">
      <PanelCard className="p-4 sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
              Filter summary
            </p>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              Showing:{" "}
              <span className="font-semibold text-slate-900 dark:text-white">
                {rangeLabel}
              </span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyPreset(preset.id)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  activePreset === preset.id
                    ? "bg-emerald-600 text-white shadow-sm shadow-emerald-600/20"
                    : "border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      </PanelCard>

      <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-emerald-500/[0.06] p-6 dark:border-slate-700/80 dark:from-slate-800/80 dark:via-slate-800/60 dark:to-emerald-500/10">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
              <LocalHospitalOutlinedIcon sx={{ fontSize: 26 }} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
                Clinic treatments
              </p>
              <p className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                Treatment summary & report
              </p>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Period:{" "}
                <span className="font-medium text-slate-700 dark:text-slate-300">
                  {rangeLabel}
                </span>
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:min-w-[280px]">
            <div className="rounded-xl border border-slate-200/80 bg-white/80 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/60">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Lines
              </p>
              <p className="text-lg font-bold tabular-nums text-slate-900 dark:text-white">
                {loading ? "…" : lineCount.toLocaleString()}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200/80 bg-white/80 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/60">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Procedures
              </p>
              <p className="text-lg font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
                {loading ? "…" : totalProcedures.toLocaleString()}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200/80 bg-white/80 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/60 col-span-2 sm:col-span-1">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Total value
              </p>
              <p className="text-lg font-bold tabular-nums text-slate-900 dark:text-white">
                {loading ? "…" : formatAmount(summary.totalValue)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <MetricTile
          label="Procedures"
          value={loading ? "…" : totalProcedures.toLocaleString()}
          hint="Includes quantities"
          accent
        />
        <MetricTile
          label="Cases"
          value={loading ? "…" : Number(summary.caseCount ?? 0).toLocaleString()}
          hint="Distinct treatment records"
        />
        <MetricTile
          label="Total value"
          value={loading ? "…" : formatAmount(summary.totalValue)}
        />
        <MetricTile
          label="Paid"
          value={loading ? "…" : Number(summary.paidCount ?? 0).toLocaleString()}
        />
        <MetricTile
          label="Planned"
          value={loading ? "…" : Number(summary.plannedCount ?? 0).toLocaleString()}
        />
        <MetricTile
          label="Completed"
          value={loading ? "…" : Number(summary.completeCount ?? 0).toLocaleString()}
        />
      </div>

      <TreatmentReportCharts trend={trend} breakdown={breakdown} loading={loading} />

      <div className="grid gap-6 lg:grid-cols-2">
        <PanelCard className="p-5 sm:p-6">
          <SectionHeader
            icon={CategoryOutlinedIcon}
            title="Procedure breakdown"
            subtitle="By treatment type in the selected period"
          />
          {loading ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-400">No procedures in this period.</p>
          ) : (
            <>
              {rows.length > 15 ? (
                <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
                  {rows.length} treatment types — scroll to see all
                </p>
              ) : null}
              <div className="max-h-[calc(2.75rem*15+2.75rem)] overflow-y-auto overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-700/60">
              <table className="min-w-full text-left text-sm">
                <thead className="sticky top-0 z-[1] bg-slate-50 text-[11px] font-bold uppercase tracking-wide text-slate-500 shadow-[0_1px_0_0_rgb(226_232_240)] dark:bg-slate-900/95 dark:text-slate-400 dark:shadow-[0_1px_0_0_rgb(51_65_85)]">
                  <tr>
                    <th className="px-4 py-3">Treatment</th>
                    <th className="px-4 py-3 text-right">Count</th>
                    <th className="px-4 py-3 text-right">Share</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                  {rows.map((row, index) => (
                    <tr
                      key={`${row.label}-${index}`}
                      className="hover:bg-slate-50/80 dark:hover:bg-slate-700/20"
                    >
                      <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">
                        <span className="inline-flex items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: row.color }}
                          />
                          {row.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {row.value.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-500">
                        {row.percent.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </PanelCard>

        <PanelCard className="p-5 sm:p-6">
          <SectionHeader
            icon={InsightsOutlinedIcon}
            title="At a glance"
            subtitle="Highlights for the filtered period"
          />
          <ul className="space-y-2.5">
            {loading ? (
              <li className="text-sm text-slate-400">Loading insights…</li>
            ) : insights.length === 0 ? (
              <li className="text-sm text-slate-400">No treatment data yet.</li>
            ) : (
              insights.map((line) => (
                <li
                  key={line}
                  className="flex gap-2 text-sm text-slate-600 dark:text-slate-300"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                  <span>{line}</span>
                </li>
              ))
            )}
          </ul>
          <div className="mt-5 grid grid-cols-3 gap-2">
            <div className="rounded-lg bg-slate-50 px-3 py-2 text-center dark:bg-slate-900/40">
              <EventNoteOutlinedIcon sx={{ fontSize: 18 }} className="text-slate-400" />
              <p className="mt-1 text-xs text-slate-500">Ongoing</p>
              <p className="font-bold tabular-nums text-slate-800 dark:text-slate-100">
                {loading ? "…" : Number(summary.ongoingCount ?? 0)}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50 px-3 py-2 text-center dark:bg-slate-900/40">
              <PaidOutlinedIcon sx={{ fontSize: 18 }} className="text-emerald-500" />
              <p className="mt-1 text-xs text-slate-500">Paid</p>
              <p className="font-bold tabular-nums text-slate-800 dark:text-slate-100">
                {loading ? "…" : Number(summary.paidCount ?? 0)}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50 px-3 py-2 text-center dark:bg-slate-900/40">
              <AttachMoneyOutlinedIcon sx={{ fontSize: 18 }} className="text-slate-400" />
              <p className="mt-1 text-xs text-slate-500">Value</p>
              <p className="font-bold tabular-nums text-slate-800 dark:text-slate-100">
                {loading ? "…" : formatAmount(summary.totalValue)}
              </p>
            </div>
          </div>
        </PanelCard>
      </div>

      <PanelCard className="p-5 sm:p-6">
        <SectionHeader
          icon={AssignmentOutlinedIcon}
          title="Procedure detail"
          subtitle="All lines in the selected period (newest first)"
        />
        {linesTruncated ? (
          <p className="mb-3 text-xs text-amber-700 dark:text-amber-300">
            Showing first 500 lines. Download the PDF for the complete list (
            {lineCount.toLocaleString()} total).
          </p>
        ) : null}
        {loading ? (
          <p className="text-sm text-slate-400">Loading detail…</p>
        ) : lines.length === 0 ? (
          <p className="text-sm text-slate-400">No procedure lines in this period.</p>
        ) : (
          <div className="max-h-[420px] overflow-auto rounded-xl border border-slate-100 dark:border-slate-700/60">
            <table className="min-w-full text-left text-sm">
              <thead className="sticky top-0 z-[1] bg-slate-50 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:bg-slate-900/95 dark:text-slate-400">
                <tr>
                  <th className="px-3 py-2.5">Date</th>
                  <th className="px-3 py-2.5">Patient</th>
                  <th className="px-3 py-2.5">Procedure</th>
                  <th className="px-3 py-2.5">Dentist</th>
                  <th className="px-3 py-2.5 text-right">Qty</th>
                  <th className="px-3 py-2.5 text-right">Value</th>
                  <th className="px-3 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                {lines.map((line, index) => (
                  <tr
                    key={`${line.caseId}-${line.lineDate}-${index}`}
                    className="hover:bg-slate-50/80 dark:hover:bg-slate-700/20"
                  >
                    <td className="whitespace-nowrap px-3 py-2 text-slate-600 dark:text-slate-300">
                      {formatLineDate(line.lineDate)}
                    </td>
                    <td className="px-3 py-2">
                      <p className="font-medium text-slate-800 dark:text-slate-100">
                        {line.patientName || "—"}
                      </p>
                      {line.invoiceNo ? (
                        <p className="text-[10px] text-slate-400">#{line.invoiceNo}</p>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-slate-700 dark:text-slate-200">
                      {line.treatmentName || "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-300">
                      {line.dentistName?.trim() || "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {line.quantity ?? 1}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatAmount(line.lineValue)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                          String(line.status).toLowerCase() === "planned"
                            ? "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                            : String(line.status).toLowerCase() === "complete" ||
                                String(line.status).toLowerCase() === "completed"
                              ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                              : "bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300"
                        }`}
                      >
                        {capitalizeStatus(line.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PanelCard>

      <PanelCard className="p-5 sm:p-6 xl:p-7">
        <SectionHeader
          icon={PictureAsPdfOutlinedIcon}
          title="Download treatment report"
          subtitle="PDF with every procedure line, patient, dentist, and summary"
        />
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <div className="flex flex-wrap gap-3">
              <FormDate label="From" name="startDate" />
              <FormDate label="To" name="endDate" />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={downloading}
                className="inline-flex h-9 items-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-semibold text-white shadow-md shadow-emerald-600/20 transition hover:opacity-95 disabled:opacity-60"
              >
                <PictureAsPdfOutlinedIcon sx={{ fontSize: 18 }} />
                {downloading ? "Preparing…" : "Download PDF"}
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex h-9 items-center rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                Reset dates
              </button>
            </div>
          </form>
        </FormProvider>
        <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
          Filters use each procedure&apos;s recorded date. Presets above refresh the
          on-screen summary; pick custom dates here to export the full PDF for that
          range.
        </p>
      </PanelCard>
    </div>
  );
}
